import * as chrono from 'chrono-node';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce, useUpdateEffect } from 'react-use';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { Input } from '../form/controls/Input';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { IconButton } from './button/IconButton';

const Header = styled(Box)`
	border-bottom: 1px solid ${tokens.border.subtle};
`;

const Toolbar = styled(Flex)``;

const MonthTitle = styled(Box)`
	font-size: 16px;
	font-weight: bold;
	text-align: center;
	user-select: none;
`;

const Week = styled(Flex)``;

const WeekNumber = styled(Box)`
	text-align: center;
	background: ${(props) => props.theme.colors.lightBlueLight};
	flex-basis: 0;
	flex-grow: 1;
	flex-shrink: 1;
	font-size: 12px;
	user-select: none;
`;

const DayNumberBadge = styled(Box)<{ $background: string }>`
	background: ${(props) => props.$background};
	width: 24px;
	height: 24px;
	position: absolute;
	top: 50%;
	left: 50%;
	margin: -12px;
	border-radius: 12px;
`;

const DayNumberText = styled(Box)<{
	badge?: boolean;
	highlighted?: boolean;
	included?: boolean;
	$disabled?: boolean;
}>`
	position: relative;
	color: ${(props) =>
		props.badge
			? props.theme.colors.white
			: props.highlighted
				? tokens.brand.text
				: props.$disabled || !props.included
					? props.theme.colors.lightBlueDark
					: 'black'};
	${(props) => props.highlighted && `font-weight: ${tokens.typography.weights.medium};`}
	${(props) => props.$disabled && !props.badge && 'opacity: 0.5;'}
`;

const DayNumber = styled(Box)<{ hoverable?: boolean; $disabled?: boolean }>`
	text-align: center;
	flex-basis: 0;
	flex-grow: 1;
	flex-shrink: 1;
	font-size: 12px;
	position: relative;
	background-color: transparent;
	border: 0;
	cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};

	${(props) =>
		props.hoverable &&
		`&:hover {
		${DayNumberBadge} {
			background: ${props.theme.colors.lightBlueLight};
		}

		${DayNumberText} {
			color: ${tokens.text.primary};
		}
	}`}
`;

type DayPredicate = (date: Date) => boolean;

function DatePickerDay(props: {
	start: moment.Moment;
	end: moment.Moment;
	date: moment.Moment;
	value?: moment.Moment;
	isHighlighted?: DayPredicate;
	isDisabled?: DayPredicate;
	onClick?: () => void;
}) {
	const theme = useTheme();
	const selected = props.value && props.date.isSame(props.value, 'day');
	const today = props.date.isSame(moment(), 'day');
	const included = props.date.isSameOrAfter(props.start) && props.date.isSameOrBefore(props.end);
	const native = props.date.toDate();
	const disabled = !!props.isDisabled?.(native);
	const highlighted = !!props.isHighlighted?.(native);

	return (
		<DayNumber
			role="button"
			aria-disabled={disabled || undefined}
			aria-pressed={selected || undefined}
			tabIndex={disabled ? -1 : 0}
			onClick={() => {
				if (!disabled) {
					props.onClick?.();
				}
			}}
			onKeyDown={(event: React.KeyboardEvent) => {
				if (disabled || (event.key !== 'Enter' && event.key !== ' ')) {
					return;
				}
				event.preventDefault();
				props.onClick?.();
			}}
			hoverable={!selected && !today && !disabled}
			$disabled={disabled}
		>
			<Box my={2}>
				<DayNumberBadge
					$background={
						selected
							? theme.colors.primary
							: today
								? tokens.text.secondary
								: highlighted
									? tokens.brand.background
									: 'transparent'
					}
				/>
				<DayNumberText
					badge={selected || today}
					highlighted={highlighted && !selected && !today}
					included={included}
					$disabled={disabled}
				>
					{props.date.date()}
				</DayNumberText>
			</Box>
		</DayNumber>
	);
}

function DatePickerWeek(props: {
	start: moment.Moment;
	end: moment.Moment;
	date: moment.Moment;
	value?: moment.Moment;
	last?: boolean;
	isHighlighted?: DayPredicate;
	isDisabled?: DayPredicate;
	onChange: (date: moment.Moment) => void;
}) {
	return (
		<Week pr={2} alignItems="flex-start">
			<WeekNumber pl={2} py={2} pb={props.last ? 3 : 2}>
				{props.date.week()}
			</WeekNumber>
			{[0, 1, 2, 3, 4, 5, 6].map((day) => (
				<DatePickerDay
					key={day}
					start={props.start}
					end={props.end}
					date={props.date.clone().add(day, 'days')}
					value={props.value}
					isHighlighted={props.isHighlighted}
					isDisabled={props.isDisabled}
					onClick={() => {
						props.onChange(props.date.clone().add(day, 'days'));
					}}
				/>
			))}
		</Week>
	);
}

function DatePickerMonth(props: {
	date: moment.Moment;
	value?: moment.Moment;
	isHighlighted?: DayPredicate;
	isDisabled?: DayPredicate;
	onChange: (date: moment.Moment) => void;
}) {
	const week = props.date.clone().startOf('month').startOf('week');
	const start = props.date.clone().startOf('month');
	const end = props.date.clone().endOf('month');

	const dates: moment.Moment[] = [];

	let last = week.clone();

	while (last.isSameOrBefore(end) || dates.length < 6) {
		dates.push(last);

		last = last.clone().add(1, 'week');
	}

	return (
		<>
			{dates.map((date, i) => (
				<DatePickerWeek
					key={i}
					start={start}
					end={end}
					date={date}
					value={props.value}
					isHighlighted={props.isHighlighted}
					isDisabled={props.isDisabled}
					onChange={props.onChange}
				/>
			))}
		</>
	);
}

export function DatePicker(props: {
	value?: Date;
	onChange: (date?: Date) => void;
	onDismiss: () => void;
	/** Gives a day a colored circle, e.g. days that have data. */
	isDayHighlighted?: DayPredicate;
	/** Makes a day unselectable; the search input also refuses it. */
	isDayDisabled?: DayPredicate;
}) {
	const [query, setQuery] = useState('');
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	const [date, setDate] = useState<Date | undefined>(props.value);
	const [anchor, setAnchor] = useState<moment.Moment>(date ? moment(date) : moment());

	useUpdateEffect(() => {
		props.onChange(date);
	}, [date]);

	useEffect(() => {
		if (query === '') {
			return;
		}
		setLoading(true);
	}, [query]);

	useDebounce(
		() => {
			if (query === '') {
				return;
			}

			const date = chrono.nl.parseDate(query);

			if (date && date.valueOf()) {
				setAnchor(moment(date));
				if (!props.isDayDisabled?.(date)) {
					setDate(date);
				}
			}

			setLoading(false);
		},
		200,
		[query],
	);

	return (
		<Box width={260}>
			<Header p={2}>
				<Input
					small
					loading={loading}
					icon={<SearchIcon />}
					value={query}
					onChange={setQuery}
					autoFocus
					autoComplete="off"
					placeholder={t('datePicker.query.placeholder')}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							props.onDismiss();
						}
					}}
				/>
			</Header>
			<Toolbar p={3} alignItems="center">
				<IconButton
					type="secondary"
					icon={<ChevronLeftIcon />}
					small
					onClick={() => {
						setAnchor(moment(anchor).subtract('1', 'month'));
					}}
				/>
				<MonthTitle flexGrow={1} flexShrink={1}>
					{anchor.format('MMMM YYYY')}
				</MonthTitle>
				<IconButton
					type="secondary"
					icon={<ChevronRightIcon />}
					small
					onClick={() => {
						setAnchor(moment(anchor).add('1', 'month'));
					}}
				/>
			</Toolbar>
			<DatePickerMonth
				date={anchor}
				value={moment(date)}
				isHighlighted={props.isDayHighlighted}
				isDisabled={props.isDayDisabled}
				onChange={(newDate) => {
					setDate(newDate.toDate());
					props.onDismiss();
				}}
			/>
		</Box>
	);
}
